import {
  type AssetSymbol,
  type ChainflipChain,
  getInternalAsset,
  type ChainflipAsset,
} from '@chainflip/utils/chainflip';
import { type BoostBalanceInfo } from '@/shared/hooks/useBoostPoolInfo';
import useChainflipAssetPrices from '@/shared/hooks/useChainflipAssetPrices';
import { useLpProcessorLpId } from '@/shared/hooks/useLpProcessorLpId';
import { type InjectedAccountWithMeta, type ReadonlyAccount } from '@/shared/hooks/usePolkadot';
import { createStateChainAccountContext } from '@/shared/hooks/useStateChainAccount';
import { chainAssetMapToInternalAssetMap } from '@/shared/utils/chainflip';
import { entries } from '@/shared/utils/objects';
import TokenAmount from '@/shared/utils/TokenAmount';
import { POLKADOT_JS_APP_NAME } from '../utils/consts';

type AccountInfo = {
  totalBalance: number | null;
  freeBalances: Record<ChainflipAsset, TokenAmount> | null;
  boostBalances: BoostBalanceInfo[] | null;
  collateralBalances: Partial<Record<ChainflipAsset, TokenAmount>> | null;
};

const useAdditionalInfo = (account: InjectedAccountWithMeta | ReadonlyAccount | null) => {
  const lpId = useLpProcessorLpId(account?.idSs58);
  const { prices: assetPrices } = useChainflipAssetPrices();
  return { lpId, assetPrices };
};

export const { StateChainAccountProvider, useStateChainAccount, useExtrinsicSDK } =
  createStateChainAccountContext({
    parseAccountInfo: (account, { assetPrices }): AccountInfo => {
      let freeBalances: Record<ChainflipAsset, TokenAmount> | null = null;
      let boostBalances: BoostBalanceInfo[] | null = null;
      let collateralBalances: Partial<Record<ChainflipAsset, TokenAmount>> | null = null;

      if (account) {
        freeBalances = chainAssetMapToInternalAssetMap(account.asset_balances, (asset, value) =>
          TokenAmount.fromAsset(value, asset),
        );

        if (account.role === 'liquidity_provider') {
          collateralBalances = Object.fromEntries(
            account.collateral_balances.map((item) => {
              const asset = getInternalAsset(item);
              return [asset, TokenAmount.fromAsset(item.amount, asset)];
            }),
          );
          boostBalances = Object.entries(account.boost_balances).flatMap(([chain, info]) =>
            Object.entries(info).flatMap(([symbol, balances]) => {
              const asset = getInternalAsset({
                chain: chain as ChainflipChain,
                asset: symbol as AssetSymbol,
              });

              return balances.map((balance) => {
                const availableAmount = TokenAmount.fromAsset(balance.available_balance, asset);
                const unavailableAmount = TokenAmount.fromAsset(balance.in_use_balance, asset);

                return {
                  pool: { asset, feeTierPips: balance.fee_tier },
                  isWithdrawing: balance.is_withdrawing,
                  availableAmount: balance.available_balance.toString(),
                  availableAmountValueUsd: availableAmount.mul(assetPrices[asset] ?? 0).toFixed(),
                  unavailableAmount: balance.in_use_balance.toString(),
                  unavailableAmountValueUsd: unavailableAmount
                    .mul(assetPrices[asset] ?? 0)
                    .toFixed(),
                } as BoostBalanceInfo;
              });
            }),
          );
        }
      }

      let totalBalance: number | null = null;

      if (freeBalances) {
        totalBalance = 0;
        for (const [asset, amount] of entries(freeBalances)) {
          const usd = amount.mul(assetPrices[asset] ?? 0).toFixed();
          totalBalance += Number(usd);
        }
      }

      return { totalBalance, freeBalances, boostBalances, collateralBalances };
    },
    polkadotAppName: POLKADOT_JS_APP_NAME,
    onboardedRole: 'liquidity_provider',
    useAdditionalInfo,
  });

export { type DepositChannel } from '@/shared/hooks/useStateChainAccount';
